import { formatDate } from '@angular/common'
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'
import { ChatDialogService } from '../services/chat-dialog.service'
import { Store } from '@ngrx/store'
import {
  AbstractComponent,
  ChatChannelTypeEnum,
  ChatCommandTypeEnum,
  ChatConversation,
  ChatMessage,
  ChatMessageTypeEnum,
  ChatService,
  ChatSocketService,
  ChatUserDisplayMap,
  RoleUserEnum,
} from 'lib-trend-core'
import { Subject, of } from 'rxjs'
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators'
import {
  ChatHighlightSegment,
  buildExcerpt,
  highlightSegments,
} from '../chat-switcher/chat-fuzzy.util'
import {
  chatFileIcon,
  chatMessageTypeFromContentType,
  formatChatFileSize,
} from '../chat-composer/chat-attachment.util'
import {
  ChatComposerComponent,
  ChatComposerSend,
  ChatMentionMember,
} from '../chat-composer/chat-composer.component'
import {
  ChatChannelInviteDialogComponent,
  ChatChannelInviteDialogData,
} from '../dialogs/chat-channel-invite-dialog/chat-channel-invite-dialog.component'
import {
  ChatChannelMembersDialogComponent,
  ChatChannelMembersDialogData,
} from '../dialogs/chat-channel-members-dialog/chat-channel-members-dialog.component'
import { ChatConfirmDialogComponent } from '../dialogs/chat-confirm-dialog/chat-confirm-dialog.component'
import { collectChatImages, openChatImageViewer } from '../dialogs/chat-image-viewer-dialog/chat-image-viewer.opener'
import { ChatAttentiveRead, isDocumentAttentive } from './chat-attentive-read.util'
import { ChatMentionRender } from '../pipes/chat-markdown.renderer'
import {
  ChatForwardDialogComponent,
  ChatForwardDialogData,
} from '../dialogs/chat-forward-dialog/chat-forward-dialog.component'
import { stripChatMarkdown } from '../pipes/chat-preview.util'
import * as ChatActions from '../state/chat.actions'
import {
  composerDraftsSelector,
  conversationsSelector,
  presenceViewsSelector,
  savedIdSetSelector,
  threadSeenSelector,
  topEmojisSelector,
  unreadCountMapSelector,
  userDisplaySelector,
} from '../state/chat.selectors'
import { ChatComposerDraft } from '../state/chat.state'
import { ChatPresenceView } from '../chat-status/chat-status.util'
import { resolveChatInitials } from '../chat-initials.util'

/** Local send-state decoration over the wire ChatMessage shape (T-19/T-33). */
type ChatMessageView = ChatMessage & {
  sendState?: 'sending' | 'sent' | 'failed'
  deleted?: boolean
  /**
   * Base clientMessageId shared by every optimistic message of ONE send —
   * an attachment send splits into several messages (T-33) but travels as a
   * single MESSAGE_SEND command, so ack/retry/discard operate on the group.
   */
  sendGroup?: string
}

/** Flattened timeline row — day divider or message. */
interface TimelineRow {
  kind: 'divider' | 'unread' | 'message'
  key: string
  label?: string
  message?: ChatMessageView
  authorName?: string
  authorInitial?: string
  authorImage?: string
  timeLabel?: string
  showAuthor?: boolean
  own?: boolean
  /** Not settled server-side yet (or failed) — reactions/actions disabled. */
  pending?: boolean
  quoteAuthor?: string
  quoteText?: string
  quoteMissing?: boolean
  reactions?: Array<{ emoji: string; count: number; mine: boolean }>
  /** Media card view-model (T-33) — non-TEXT messages only. */
  fileIcon?: string
  fileSizeLabel?: string
  // T-20 message-action state ------------------------------------------------
  /** Bookmarked by me (RF-14) — flips the toolbar's save action. */
  saved?: boolean
  /** Present in `channel.pinnedMessages` (RF-31). */
  pinned?: boolean
  /** Thread replies exist that I have not opened yet (client-side, T-20). */
  threadUnseen?: boolean
  /** Last thread reply time shown next to the chip (RF-12/AC-10). */
  threadTimeLabel?: string
  /** Plain-text body offered to "Copiar texto" / quote excerpts. */
  copyText?: string
  /** `@Name` tokens the chatMarkdown pipe turns into chips. */
  mentionRender?: ChatMentionRender[]
  /** The message mentions the viewing user — tints the whole row. */
  mentionsMe?: boolean
  /**
   * Forwarded banner (RF-13) — "Encaminhada de X" when the original author
   * resolves, plain "Encaminhada" otherwise (pre-snapshot copies carry only
   * the source id).
   */
  forwardedLabel?: string
}

/** Pinned bar entry (RF-31) — resolved from the loaded timeline when possible. */
interface ChatPinnedRow {
  messageId: string
  authorName: string
  excerpt: string
  /** Not in the loaded page — the label stays neutral, jump pages back. */
  unresolved: boolean
}

/** Quote-reply strip above the composer (RF-30). */
interface ChatQuoteTarget {
  messageId: string
  authorName: string
  excerpt: string
}

const HISTORY_PAGE_LIMIT = 50
const PREVIEW_MAX_LENGTH = 140
const QUOTE_EXCERPT_MAX_LENGTH = 90
const SCROLL_TOP_THRESHOLD_PX = 80
const SCROLL_BOTTOM_THRESHOLD_PX = 150
const TYPING_CLEAR_MS = 4000
const DRAFT_PERSIST_DEBOUNCE_MS = 300

/** How long a jumped-to message keeps its highlight. */
const JUMP_HIGHLIGHT_MS = 2500

/** Max extra history pages fetched while seeking a jump target (pin/saved). */
const JUMP_MAX_PAGES = 5

/** Quick reactions when the user has no reaction history yet (RF-11). */
const DEFAULT_QUICK_REACTIONS: string[] = ['👍', '✅', '🚀']

/** How many of the user's most-used emojis lead the reaction bar (RF-11). */
const QUICK_REACTION_COUNT = 3

/** Optimistic ids before the server assigns the real `_id` (fan-out reconciles). */
const PENDING_ID_PREFIX = 'pending:'

/** Below this the in-conversation query is too noisy to be worth a round trip. */
const SEARCH_MIN_LENGTH = 2

/** Hits listed in the header dropdown — it is a menu, not a results page. */
const SEARCH_HIT_LIMIT = 8

/**
 * Excerpt half-window, in characters. Small because each hit is one truncated
 * line inside a dropdown: a wide window would push the highlighted match past the
 * ellipsis, which is the one thing the highlight exists to prevent.
 */
const SEARCH_EXCERPT_RADIUS = 24

/** One hit of the in-conversation search, as the header dropdown renders it. */
interface ChatConversationSearchHit {
  messageId: string
  authorName: string
  timeLabel: string
  segments: ChatHighlightSegment[]
}

/**
 * Internal Chat conversation pane (SPEC-2026-07-internal-chat, T-18) — shared
 * by the `/chat` full view and the bubble popup (compact mode).
 *
 * HARD ARCHITECTURAL RULE (attendance-panel-chat production incident): the
 * open conversation's messages live in a COMPONENT-LOCAL array — hydrated once
 * per channel from REST, then socket events append/patch locally with dedup by
 * `_id` (and by `clientMessageId` for own optimistic sends). The full list is
 * NEVER re-dispatched to the store per message.
 *
 * History pages back with the OPAQUE `meta.nextCursor` (echoed verbatim into
 * `before`), preserving the scroll anchor. On reconnection the latest page is
 * refetched and merge-deduped; when no overlap with the local tail exists the
 * timeline resets to the fetched page (see reconnect note in `refetchLatest`).
 *
 * T-19 additions hosted here:
 * - message bodies render through the shared `chatMarkdown` pipe (escape-first
 *   markdown subset — see pipes/chat-markdown.renderer.ts);
 * - send states driven by `chat:ack`: sending (clock) → sent (check) on ack
 *   success, failed row with retry (re-sends the SAME `clientMessageId`, so
 *   server-side idempotency prevents duplication) and discard;
 * - mention members of the active conversation feed the composer autocomplete;
 * - per-conversation composer drafts persisted in the store (restored on
 *   switch; key = channelId | `direct:{userId}`).
 *
 * T-33 additions hosted here:
 * - drag-and-drop overlay feeding the composer's addFiles() (limits included);
 * - optimistic rendering of an attachment send MIRRORS the backend split
 *   (message.service.ts processSend): one media message per attachment with
 *   `clientMessageId` suffixed `-0..-9`, plus one TEXT message (unsuffixed)
 *   when content exists — all sharing ONE MESSAGE_SEND command whose payload
 *   is kept per group for idempotent retries;
 * - media cards: image thumbnail (opens original), inline video, document
 *   file card. AUDIO renders the T-34 `chat-audio-player` (deterministic
 *   pseudo-waveform seeded by clientMessageId, scrub, 1×/1.5×/2× speed).
 *
 * T-20 additions hosted here (RF-09…RF-14, RF-30, RF-31):
 * - per-row hover/focus action toolbar: the user's top-3 emojis (derived from
 *   their own reactions across the loaded page, `DEFAULT_QUICK_REACTIONS` until
 *   they have a history) + the full picker, thread reply, quote reply, forward,
 *   save, pin/unpin and an overflow menu (edit/delete on own messages, reply in
 *   private, copy text);
 * - THREAD PANEL: `chat-thread` docked as a right drawer (full view) or as a
 *   full-cover swap (`compact` popup). Thread replies are kept OUT of the main
 *   flow — the read endpoint filters `parentMessage != null`, so the socket
 *   fan-out of a reply only bumps the root's chip here;
 * - inline edit (MESSAGE_EDIT) and delete through the shared Hyper
 *   ChatConfirmDialogComponent (MESSAGE_DELETE → tombstone via the fan-out);
 * - pinned bar from `channel.pinnedMessages` with jump/unpin (the 50-pin
 *   refusal surfaces as `chat.errors.pinLimitReached`);
 * - jump-to-message with highlight, seeking up to `JUMP_MAX_PAGES` older pages
 *   for targets outside the loaded window (pinned bar, saved view).
 */
@Component({
  selector: 'chat-conversation',
  templateUrl: './chat-conversation.component.html',
  styleUrls: ['./chat-conversation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatConversationComponent
  extends AbstractComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLDivElement>
  @ViewChild(ChatComposerComponent) composer: ChatComposerComponent

  /** Open conversation id — null in DIRECT-draft mode. */
  @Input() channelId: string | null = null

  /** DIRECT draft counterpart (no channel yet — first send materializes it). */
  @Input() directUserId: string | null = null

  /** Popup single-pane mode (tighter paddings). */
  @Input() compact = false

  /**
   * Message to reveal once the history is loaded (T-20) — set by the saved view
   * jumping into a conversation. Targets outside the loaded window trigger a
   * bounded older-page seek.
   */
  @Input() focusMessageId: string | null = null

  /**
   * Thread to show open (SPEC-2026-09-internal-chat-pwa, RF-02) — the chat app
   * restores it from the address, so a reload keeps the same thread on screen.
   * Absent on the product's surfaces, which open threads from the timeline only.
   */
  @Input() threadRootId: string | null = null

  /** The open thread changed from inside the pane — the chat app mirrors it into the address. */
  @Output() threadOpenChange = new EventEmitter<string | null>()

  /** Renders a back control in the header — the chat app's phone layout (RF-26). */
  @Input() showBack = false

  /** The back control was activated. */
  @Output() back = new EventEmitter<void>()

  /** Full emoji picker of the reaction dropdown (after the user's top-3). */
  readonly quickReactions: string[] = [
    '👍',
    '❤️',
    '😂',
    '🎉',
    '👀',
    '✅',
    '🙏',
    '🔥',
    '💪',
    '😢',
    '😮',
    '🚀',
  ]

  /** The user's three most-used emojis, leading the reaction bar (RF-11). */
  topReactions: string[] = [...DEFAULT_QUICK_REACTIONS]

  // --- In-conversation search (header dropdown) ------------------------------
  // Scoped to THIS conversation via the endpoint's `in` filter, which the server
  // intersects with the caller's allowlist. Picking a hit jumps to the message in
  // place — `jumpToMessage` seeks extra history pages when it is not loaded yet —
  // so the pane never has to be remounted to show a result.

  searchTerm = ''

  searchHits: ChatConversationSearchHit[] = []

  searching = false

  private readonly searchTerms = new Subject<string>()

  /** Store snapshot of the authoritative reaction ranking (bootstrap-fed). */
  private topEmojis: string[] = []

  /** Exposes ChatMessageTypeEnum to the template's media-card switch (T-33). */
  readonly messageType = ChatMessageTypeEnum

  // Component-local timeline (NEVER in the store — see class JSDoc).
  messages: ChatMessageView[] = []
  rows: TimelineRow[] = []

  /** Pinned bar entries (RF-31) — rebuilt from the channel + loaded messages. */
  pinnedRows: ChatPinnedRow[] = []

  /** Thread root open in the panel (null = closed). */
  openThreadRootId: string | null = null

  /** Deferred READ while the page is hidden or unfocused (RF-22). */
  private readonly attentiveRead = new ChatAttentiveRead(() =>
    isDocumentAttentive(typeof document === 'undefined' ? null : document),
  )

  /** Quote-reply target shown above the composer (RF-30). */
  quoteTarget: ChatQuoteTarget | null = null

  /** Message being edited in place (own messages only, RF-09). */
  editingMessageId: string | null = null
  editDraft = ''

  /** Message highlighted by a jump (pinned bar, quote, saved view). */
  highlightedMessageId: string | null = null

  /** Drag-and-drop overlay visibility (T-33). */
  dragActive = false
  private dragDepth = 0

  /**
   * MESSAGE_SEND payload per base clientMessageId (T-33) — kept while the send
   * is unsettled so retry re-emits the EXACT same command (idempotent
   * server-side); dropped on ack success or discard.
   */
  private readonly sendPayloads = new Map<string, Record<string, unknown>>()

  loadingHistory = false
  loadingOlder = false
  loadError = false
  hasMoreHistory = false
  typingLabel: string | null = null

  conversation: ChatConversation | null = null

  /** Members offered by the composer's `@` autocomplete (excludes self). */
  mentionMembers: ChatMentionMember[] = []

  private conversations: ChatConversation[] = []
  private userDisplay: ChatUserDisplayMap = {}
  /** Precomputed presence views (dot class + tooltip parts), userId-keyed. */
  private presenceViews: Record<string, ChatPresenceView> = {}
  private composerDrafts: Record<string, ChatComposerDraft> = {}

  // --- Unread line + jump pill -------------------------------------------------
  /** channelId → unread count, mirrored from the store. */
  private unreadCounts: Record<string, number> = {}
  /**
   * Unread count captured at the moment the conversation opened — `markRead()`
   * clears the store entry immediately after, so the line has to remember it.
   */
  private unreadAtOpen = 0
  /** Message the "Novas mensagens" line renders above. Cleared on switch. */
  firstUnreadMessageId: string | null = null
  /** Messages that arrived while scrolled up — the jump pill's counter. */
  newSinceScroll = 0
  showJumpPill = false

  /** Bookmarked message ids (T-20) — toolbar save/unsave state. */
  private savedIds = new Set<string>()

  /** Thread root id → reply count already seen (T-20 unseen accent). */
  private threadSeen: Record<string, number> = {}

  /** Draft-store key of the conversation currently hosted (T-19). */
  private draftKey: string | null = null
  private readonly draftPersist$ = new Subject<void>()

  /** Opaque cursor of the OLDER history direction (echoed verbatim). */
  private historyCursor: string | null = null

  private typingClearTimer: ReturnType<typeof setTimeout> | null = null
  private highlightClearTimer: ReturnType<typeof setTimeout> | null = null

  /** Jump target still awaiting the initial history load (T-20). */
  private pendingFocusMessageId: string | null = null

  constructor(
    injector: Injector,
    private readonly store: Store,
    private readonly chatService: ChatService,
    private readonly chatSocketService: ChatSocketService,
    private readonly dialog: ChatDialogService,
  ) {
    super(injector)
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngOnInit(): void {
    // `switchMap` drops the in-flight request when a newer term arrives, so hits
    // can never land out of order behind a slower earlier query.
    this.searchTerms
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          const channelId = this.channelId
          if (!channelId || term.length < SEARCH_MIN_LENGTH) {
            return of<ChatConversationSearchHit[]>([])
          }
          return this.chatService
            .search({ q: term, in: channelId, limit: SEARCH_HIT_LIMIT })
            .pipe(
              map((envelope) =>
                this.buildSearchHits(
                  envelope?.data?.messages ?? [],
                  envelope?.data?.userDisplay ?? {},
                  term,
                ),
              ),
              // A failed query degrades to "no hits" — the conversation itself is
              // still on screen and usable.
              catchError(() => of<ChatConversationSearchHit[]>([])),
            )
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((hits) => {
        this.searching = false
        this.searchHits = hits
      })

    this.store
      .select(conversationsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((conversations) => {
        this.conversations = conversations
        this.conversation = this.channelId
          ? (conversations.find((item) => item._id === this.channelId) ?? null)
          : null
        this.maybeLoadChannelRoster()
        this.rebuildMentionMembers()
        // The pin fan-out lands on the conversation document (reducer) — rebuild
        // the bar and the per-row pinned flags from the new snapshot.
        this.buildRows()
      })

    // Membership changes on the open channel invalidate the mention roster
    // (channels only — DM/GROUP rosters ride the bootstrap `members`).
    this.chatSocketService.channelUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (!data?.channelId || data.channelId !== this.channelId) return
        if (
          data.action === 'invited' ||
          data.action === 'joined' ||
          data.action === 'left'
        ) {
          this.maybeLoadChannelRoster(true)
        }
      })

    this.store
      .select(savedIdSetSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((savedIds) => {
        this.savedIds = savedIds
        this.buildRows()
      })

    this.store
      .select(threadSeenSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((threadSeen) => {
        this.threadSeen = threadSeen
        this.buildRows()
      })

    this.store
      .select(userDisplaySelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userDisplay) => {
        this.userDisplay = userDisplay
        this.rebuildMentionMembers()
        this.buildRows()
      })

    this.store
      .select(presenceViewsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((views) => (this.presenceViews = views))

    this.store
      .select(unreadCountMapSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((counts) => (this.unreadCounts = counts))

    // Authoritative reaction ranking (RF-11/AC-09) — refreshed by the bootstrap.
    this.store
      .select(topEmojisSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((topEmojis) => {
        this.topEmojis = topEmojis
        this.topReactions = this.buildTopReactions()
      })

    this.store
      .select(composerDraftsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((drafts) => (this.composerDrafts = drafts))

    // Composer keystrokes → debounced draft snapshot into the store (T-19).
    this.draftPersist$
      .pipe(debounceTime(DRAFT_PERSIST_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => this.persistDraft())

    // LOCAL APPEND of the open channel's fan-out (dedup by _id/clientMessageId).
    this.chatSocketService.messageNew$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => this.onMessageNew(message))

    this.chatSocketService.messageUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => this.onMessageUpdated(message))

    this.chatSocketService.messageDeleted$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data?.channelId !== this.channelId) return
        this.patchMessage(data.messageId, (message) => ({
          ...message,
          deleted: true,
        }))
      })

    this.chatSocketService.reactionUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data?.channelId !== this.channelId) return
        this.patchMessage(data.messageId, (message) => ({
          ...message,
          reactions: data.reactions ?? [],
        }))
      })

    this.chatSocketService.typing$
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) =>
        this.onTyping(data.channelId, data.userId, data.userName, data.typing),
      )

    // Reconnection: counters and messages may have drifted — refetch the
    // newest page and merge (see refetchLatest for the cursor rationale).
    this.chatSocketService.onReconnect$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refetchLatest())

    // Bookmarks are not part of the bootstrap snapshot, and the per-message
    // toolbar must know whether a message is already saved (RF-14). One cheap
    // capped read per pane mount; the effect's switchMap collapses bursts.
    this.store.dispatch(ChatActions.loadSaved())
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['channelId'] || changes['directUserId']) {
      // Snapshot the draft of the conversation being LEFT (this.draftKey still
      // points at it) before the composer is repurposed.
      this.persistDraft()

      const previous: string | null =
        changes['channelId']?.previousValue ?? null
      if (previous && previous !== this.channelId) {
        // Stops the reconnect re-join; live events for the old room are
        // filtered out by the channelId checks above until disconnect.
        this.chatSocketService.leaveChannel(previous)
      }
      this.resetTimeline()
      this.draftKey = this.conversationKey
      // A jump requested TOGETHER with the switch waits for the first page; a
      // value left over from a previous jump is dropped, otherwise switching
      // conversations would keep seeking a message that lives elsewhere.
      this.pendingFocusMessageId = changes['focusMessageId']
        ? this.focusMessageId
        : null
      // Roster belongs to the PREVIOUS conversation — drop it before the new
      // one resolves (maybeLoadChannelRoster refetches for channels).
      this.channelRosterIds = []
      this.rosterChannelId = null
      if (this.channelId) {
        this.conversation =
          this.conversations.find((item) => item._id === this.channelId) ?? null
        this.chatSocketService.joinChannel(this.channelId)
        this.maybeLoadChannelRoster()
        // Captured BEFORE markRead() — it clears the store entry, and the
        // "Novas mensagens" line is anchored from this snapshot once the first
        // history page lands.
        this.unreadAtOpen = this.unreadCounts[this.channelId] ?? 0
        this.loadInitial()
        this.markRead()
      }
      this.rebuildMentionMembers()
      this.restoreDraft()
      // A thread requested with the switch (an address carrying one) opens on the
      // new conversation; resetTimeline() already closed the previous one.
      if (this.channelId && this.threadRootId) {
        this.openThreadRootId = this.threadRootId
      }
      return
    }

    // Thread-only change (same conversation — back/forward through addresses).
    if (changes['threadRootId']) {
      this.openThreadRootId = this.channelId ? (this.threadRootId ?? null) : null
    }

    // Focus-only change (same conversation, e.g. two jumps from the saved view).
    if (changes['focusMessageId'] && this.focusMessageId) {
      if (this.loadingHistory || this.messages.length === 0) {
        this.pendingFocusMessageId = this.focusMessageId
      } else {
        this.jumpToMessage(this.focusMessageId)
      }
    }
  }

  ngAfterViewInit(): void {
    // First restore — ngOnChanges ran before the composer ViewChild existed.
    this.restoreDraft()
  }

  override ngOnDestroy(): void {
    // Popup closed / route left mid-typing — the draft survives in the store.
    this.persistDraft()
    if (this.channelId) {
      this.chatSocketService.leaveChannel(this.channelId)
    }
    if (this.typingClearTimer) clearTimeout(this.typingClearTimer)
    if (this.highlightClearTimer) clearTimeout(this.highlightClearTimer)
    super.ngOnDestroy()
  }

  // ---------------------------------------------------------------------------
  // Header view-model
  // ---------------------------------------------------------------------------

  get isDraft(): boolean {
    return !this.channelId && !!this.directUserId
  }

  get isDirect(): boolean {
    return (
      this.isDraft || this.conversation?.type === ChatChannelTypeEnum.DIRECT
    )
  }

  get headerName(): string {
    if (this.isDraft) {
      return (
        this.userDisplay[this.directUserId]?.name ??
        this.t('chat.bubble.directConversation')
      )
    }
    if (!this.conversation) return this.t('chat.bubble.channelConversation')
    return this.resolveConversationName(this.conversation)
  }

  get headerIcon(): string {
    const type = this.isDraft
      ? ChatChannelTypeEnum.DIRECT
      : this.conversation?.type
    switch (type) {
      case ChatChannelTypeEnum.DIRECT:
        return 'ri-user-3-line'
      case ChatChannelTypeEnum.GROUP:
        return 'ri-group-line'
      case ChatChannelTypeEnum.PRIVATE:
        return 'ri-lock-line'
      default:
        return 'ri-hashtag'
    }
  }

  get memberCount(): number {
    return this.conversation?.members?.length ?? 0
  }

  /** The other participant of a DIRECT conversation (or draft). */
  private get counterpartId(): string {
    return this.isDraft
      ? this.directUserId
      : ((this.conversation?.members ?? []).find(
          (id) => id !== this.getIDCurrentUser(),
        ) ?? this.getIDCurrentUser())
  }

  // --- Header avatar (direct: face + presence dot; channel: type glyph) -------

  get headerImage(): string | undefined {
    return this.isDirect
      ? this.userDisplay[this.counterpartId]?.image
      : undefined
  }

  get headerInitial(): string {
    return resolveChatInitials(this.headerName || '?')
  }

  get headerPresence(): ChatPresenceView | null {
    return this.isDirect
      ? (this.presenceViews[this.counterpartId] ?? null)
      : null
  }

  /**
   * The header's sub-line for a direct conversation: the counterpart's custom
   * status when one is set, nothing otherwise. The old permanent Online/Offline
   * label is gone — the avatar dot already says it, and a line that mostly read
   * "Offline" was dead weight under every name.
   */
  get headerStatusLine(): string | null {
    const view = this.headerPresence
    if (!view?.hasCustomStatus) return null
    const emoji = view.emoji ? `${view.emoji} ` : ''
    return `${emoji}${view.text}`.trim()
  }

  /**
   * Presence view for a message row's author — dot class + tooltip parts.
   * Methods, not row fields: presence/status update on socket events without
   * rebuilding rows, so the avatar has to read them at render time (the views
   * themselves are precomputed per store emission, so render stays cheap).
   */
  authorPresence(row: TimelineRow): ChatPresenceView | null {
    const sender = row.message?.sender
    return sender ? (this.presenceViews[String(sender)] ?? null) : null
  }

  /** "Nome · 🧉 status custom" — or the availability label when none is set. */
  authorTooltip(row: TimelineRow): string {
    const name = row.authorName ?? ''
    const view = this.authorPresence(row)
    if (!view) return name
    if (view.hasCustomStatus) {
      const emoji = view.emoji ? `${view.emoji} ` : ''
      return `${name} · ${emoji}${view.text}`.trim()
    }
    return `${name} · ${this.t(view.labelKey)}`
  }

  /**
   * The message the "Novas mensagens" line sits above: the `unreadAtOpen`-th
   * message from OTHERS counting back from the newest — own messages sit after
   * `lastRead` too, but the server never counts them as unread. When the whole
   * loaded page is unread the line goes to its top (older pages are reachable,
   * but seeking them for a line is not worth the scroll hijack).
   */
  private resolveFirstUnread(): string | null {
    if (this.unreadAtOpen <= 0) return null
    const me = this.getIDCurrentUser()
    let remaining = this.unreadAtOpen
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (this.messages[i].sender === me) continue
      remaining--
      if (remaining === 0) return this.messages[i]._id
    }
    return this.messages[0]?._id ?? null
  }

  private scrollToUnreadLine(): void {
    setTimeout(() => {
      const line = this.scrollContainer?.nativeElement?.querySelector(
        '[data-unread-line]',
      ) as HTMLElement | null
      if (line) {
        line.scrollIntoView({ block: 'center' })
        // Landing mid-history means the newest message is out of view — arm the
        // pill immediately instead of waiting for the first scroll event.
        this.onScroll()
      } else {
        this.scrollToBottom()
      }
    })
  }

  /** ArrowUp on an empty composer — edit my most recent editable message. */
  editLastOwn(): void {
    for (let i = this.rows.length - 1; i >= 0; i--) {
      const row = this.rows[i]
      if (row.kind !== 'message' || !row.own || row.pending) continue
      const message = row.message
      if (!message || message.deleted) continue
      if (message.type !== ChatMessageTypeEnum.TEXT) continue
      this.startEdit(row)
      setTimeout(() => {
        this.scrollContainer?.nativeElement
          ?.querySelector(`[data-message-id="${message._id}"]`)
          ?.scrollIntoView({ block: 'center' })
      })
      return
    }
  }

  /**
   * Esc hierarchy, innermost first: the edit textarea and the search dropdown
   * already consume their own Esc (the textarea stops propagation, Bootstrap
   * dismisses the dropdown); an open thread is the next layer, and only when
   * none of them claims the key does it reach the popup's document listener
   * (clear search → minimize).
   */
  /**
   * The page regained attention — send the READ deferred while it was hidden or
   * unfocused (SPEC-2026-09-internal-chat-pwa, RF-22).
   */
  @HostListener('window:focus')
  @HostListener('document:visibilitychange')
  onAttentionChange(): void {
    this.attentiveRead.flush()
  }

  @HostListener('keydown.escape', ['$event'])
  onEscape(event: Event): void {
    if (!this.openThreadRootId) return
    event.preventDefault()
    event.stopPropagation()
    this.closeThread()
  }

  get composerDisabled(): boolean {
    return !!this.conversation?.archived || this.isChannelPreview
  }

  /**
   * Read-only preview of a PUBLIC channel the user has not joined yet
   * (DEV-1984): the bootstrap lists every public channel of the company, with
   * `membership: null` for non-members. In preview the composer is replaced by
   * the "Entrar no canal" call-to-action, message actions are hidden and READ
   * is never sent (there is no membership row to stamp).
   */
  get isChannelPreview(): boolean {
    return (
      !!this.conversation &&
      this.conversation.type === ChatChannelTypeEnum.PUBLIC &&
      !this.conversation.membership
    )
  }

  /** CHANNEL_JOIN in flight from the preview call-to-action. */
  joiningChannel = false

  /**
   * Self-service join from the preview (same command the "Explorar canais"
   * dialog sends). Membership truth arrives via the `joined` fan-out plus the
   * bootstrap refresh — `membership` fills in and the composer takes over.
   */
  joinChannel(): void {
    if (!this.isChannelPreview || this.joiningChannel || !this.channelId) return

    this.joiningChannel = true
    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.CHANNEL_JOIN, { channelId: this.channelId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.joiningChannel = false
          this.store.dispatch(ChatActions.loadBootstrap())
        },
        error: (ack) => {
          this.joiningChannel = false
          this.alertService.error(
            this.translateAckError(ack?.errorKey, 'chat.errors.commandFailed'),
          )
        },
      })
  }

  // --- Channel governance (T-35 · RF-04/RF-05 · AC-04) ------------------------
  // The visibility rules mirror the backend (channel.service.ts): invites are
  // PUBLIC/PRIVATE/GROUP + active member (DEV-1984), DIRECT conversations can
  // never be left, and archiving is platform-ADMIN only.

  get canInviteMembers(): boolean {
    const type = this.conversation?.type
    return (
      !!this.conversation &&
      !!this.conversation.membership &&
      !this.conversation.archived &&
      (type === ChatChannelTypeEnum.PUBLIC ||
        type === ChatChannelTypeEnum.PRIVATE ||
        type === ChatChannelTypeEnum.GROUP)
    )
  }

  get canLeaveConversation(): boolean {
    return (
      !!this.conversation && !!this.conversation.membership && !this.isDirect
    )
  }

  /**
   * Mirrors the server-side governance guard (channel.service.ts:
   * `roles.includes(ADMIN)`) EXACTLY — NOT AbstractComponent.isAdmin(), which
   * demands the full ADMIN+SUPERVISOR+ATTENDANT triple. The user form saves
   * roles as independent checkboxes, so an ADMIN-only account would see no
   * governance action here while the server accepts its commands.
   */
  private get hasPlatformAdminRole(): boolean {
    return this.getRoles()?.includes(RoleUserEnum.ADMIN) ?? false
  }

  get canArchiveConversation(): boolean {
    return (
      !!this.conversation &&
      !this.conversation.archived &&
      !this.isDirect &&
      this.hasPlatformAdminRole
    )
  }

  /**
   * No `archived` guard, unlike archive: an archived (read-only) conversation
   * is exactly the kind an Admin eventually wants to clear out for good.
   */
  get canDeleteConversation(): boolean {
    return !!this.conversation && !this.isDirect && this.hasPlatformAdminRole
  }

  /** Whether the header overflow menu has anything to show at all. */
  get hasHeaderActions(): boolean {
    return (
      this.canInviteMembers ||
      this.canLeaveConversation ||
      this.canArchiveConversation ||
      this.canDeleteConversation
    )
  }

  /**
   * The roster view behind the clickable title: any channel or group (never
   * DIRECT — the header already IS the person). Membership is not required:
   * the endpoint itself enforces PUBLIC = company-readable, others = member.
   */
  get canViewMembers(): boolean {
    return !!this.conversation && !!this.channelId && !this.isDirect
  }

  /** Title click — read-only "Participantes" dialog (GET channels/:id/members). */
  openMembers(): void {
    if (!this.canViewMembers || !this.channelId) return
    const data: ChatChannelMembersDialogData = {
      channelId: this.channelId,
      channelName: this.headerName,
    }
    this.dialog.open(ChatChannelMembersDialogComponent, { width: '480px', data })
  }

  /** CHANNEL_INVITE — PUBLIC/PRIVATE channels and GROUPs I am an active member of. */
  inviteMembers(): void {
    if (!this.canInviteMembers || !this.channelId) return
    const data: ChatChannelInviteDialogData = {
      channelId: this.channelId,
      channelName: this.headerName,
    }
    this.dialog.open(ChatChannelInviteDialogComponent, { width: '520px', data })
  }

  /** CHANNEL_LEAVE — the conversation disappears through the `left` fan-out. */
  leaveConversation(): void {
    if (!this.canLeaveConversation || !this.channelId) return
    const channelId = this.channelId

    this.dialog
      .open(ChatConfirmDialogComponent, {
        width: '480px',
        data: this.t('chat.channel.leaveConfirm'),
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed !== true) return
        this.chatSocketService
          .sendCommand(ChatCommandTypeEnum.CHANNEL_LEAVE, { channelId })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () =>
              this.alertService.success(this.t('chat.channel.leaveSuccess')),
            error: (ack) =>
              this.alertService.error(
                this.translateAckError(
                  ack?.errorKey,
                  'chat.errors.commandFailed',
                ),
              ),
          })
      })
  }

  /** CHANNEL_ARCHIVE — platform ADMIN only; read-only for everyone afterwards. */
  archiveConversation(): void {
    if (!this.canArchiveConversation || !this.channelId) return
    const channelId = this.channelId

    this.dialog
      .open(ChatConfirmDialogComponent, {
        width: '480px',
        data: this.t('chat.channel.archiveConfirm'),
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed !== true) return
        this.chatSocketService
          .sendCommand(ChatCommandTypeEnum.CHANNEL_ARCHIVE, { channelId })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            // The archived badge arrives through the `archived` fan-out.
            next: () =>
              this.alertService.success(this.t('chat.channel.archiveSuccess')),
            // Surfaces chat.errors.adminRequired when the role check refuses.
            error: (ack) =>
              this.alertService.error(
                this.translateAckError(
                  ack?.errorKey,
                  'chat.errors.commandFailed',
                ),
              ),
          })
      })
  }

  /**
   * CHANNEL_DELETE — platform ADMIN only. The conversation disappears for
   * every member through the `deleted` fan-out: the reducer drops the row and
   * clears the active selection, and the bubble popup returns to its list.
   */
  deleteConversation(): void {
    if (!this.canDeleteConversation || !this.channelId) return
    const channelId = this.channelId

    this.dialog
      .open(ChatConfirmDialogComponent, {
        width: '480px',
        data: this.t('chat.channel.deleteConfirm'),
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed !== true) return
        this.chatSocketService
          .sendCommand(ChatCommandTypeEnum.CHANNEL_DELETE, { channelId })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () =>
              this.alertService.success(this.t('chat.channel.deleteSuccess')),
            // Surfaces chat.errors.deleteAdminRequired / cannotDeleteDirect
            // when the server-side guards refuse.
            error: (ack) =>
              this.alertService.error(
                this.translateAckError(
                  ack?.errorKey,
                  'chat.errors.commandFailed',
                ),
              ),
          })
      })
  }

  // ---------------------------------------------------------------------------
  // History loading (opaque cursors, scroll anchor preserved)
  // ---------------------------------------------------------------------------

  loadInitial(): void {
    if (!this.channelId) return
    const channelId = this.channelId
    this.loadingHistory = true
    this.loadError = false

    this.chatService
      .getMessages(channelId, { limit: HISTORY_PAGE_LIMIT })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envelope) => {
          if (channelId !== this.channelId) return // switched meanwhile
          this.messages = (envelope?.data ?? []) as ChatMessageView[]
          this.hasMoreHistory = envelope?.meta?.hasMore ?? false
          this.historyCursor = envelope?.meta?.nextCursor ?? null
          this.mergeUserDisplay(envelope?.meta?.userDisplay)
          this.loadingHistory = false
          // Anchor the "Novas mensagens" line before the rows render, so the
          // first paint already carries it (no reflow flash).
          this.firstUnreadMessageId = this.resolveFirstUnread()
          this.buildRows()
          if (this.firstUnreadMessageId) {
            // Land where reading stopped, not at the newest message — the pill
            // takes over from here (not being near the bottom is what arms it).
            this.scrollToUnreadLine()
          } else {
            this.scrollToBottom()
          }

          // A jump requested before the history existed resolves now (T-20).
          const pending = this.pendingFocusMessageId
          this.pendingFocusMessageId = null
          if (pending) this.jumpToMessage(pending)
        },
        error: () => {
          if (channelId !== this.channelId) return
          this.loadingHistory = false
          this.loadError = true
        },
      })
  }

  onScroll(): void {
    const element = this.scrollContainer?.nativeElement
    if (!element) return
    if (
      element.scrollTop <= SCROLL_TOP_THRESHOLD_PX &&
      this.hasMoreHistory &&
      !this.loadingOlder &&
      !this.loadingHistory
    ) {
      this.loadOlder()
    }

    // Jump pill: armed whenever the newest message is out of view, disarmed
    // (and its counter cleared) the moment the user reaches the bottom.
    const nearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight <=
      SCROLL_BOTTOM_THRESHOLD_PX
    this.showJumpPill = !nearBottom && this.rows.length > 0
    if (nearBottom) this.newSinceScroll = 0
  }

  jumpToLatest(): void {
    this.scrollToBottom()
    this.newSinceScroll = 0
    this.showJumpPill = false
  }

  /**
   * Pages one step back through history. `onDone(loadedNew)` lets the jump seek
   * (T-20) chain pages without duplicating the cursor bookkeeping.
   */
  private loadOlder(onDone?: (loadedNew: boolean) => void): void {
    if (!this.channelId || !this.historyCursor) {
      onDone?.(false)
      return
    }
    const channelId = this.channelId
    this.loadingOlder = true

    this.chatService
      .getMessages(channelId, {
        before: this.historyCursor,
        limit: HISTORY_PAGE_LIMIT,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envelope) => {
          if (channelId !== this.channelId) {
            onDone?.(false)
            return
          }
          const older = (envelope?.data ?? []) as ChatMessageView[]
          const known = new Set(this.messages.map((message) => message._id))
          const fresh = older.filter((message) => !known.has(message._id))

          const element = this.scrollContainer?.nativeElement
          const previousHeight = element?.scrollHeight ?? 0
          const previousTop = element?.scrollTop ?? 0

          this.messages = [...fresh, ...this.messages]
          this.hasMoreHistory = envelope?.meta?.hasMore ?? false
          this.historyCursor = envelope?.meta?.nextCursor ?? null
          this.mergeUserDisplay(envelope?.meta?.userDisplay)
          this.loadingOlder = false
          this.buildRows()

          // Preserve the visual anchor: keep the previously-visible message at
          // the same offset after the prepend grows the scroll height.
          setTimeout(() => {
            const container = this.scrollContainer?.nativeElement
            if (container) {
              container.scrollTop =
                container.scrollHeight - previousHeight + previousTop
            }
            // Deferred so a chained jump measures the grown timeline.
            onDone?.(fresh.length > 0)
          })
        },
        error: () => {
          this.loadingOlder = false
          onDone?.(false)
        },
      })
  }

  /**
   * Reconnection delta. CONTRACT NOTE: `GET /channels/:id/messages` only
   * returns `meta.nextCursor` for the direction being paged (older side on a
   * history load, and only when hasMore) — a plain latest-page load never
   * yields an `after` cursor, and cursors are opaque (never built client-side).
   * So the delta is implemented as a newest-page refetch merged by `_id`; if
   * the fetched page does not overlap the local tail (gap > page size) the
   * timeline resets to the fetched page and history re-pages on demand.
   */
  private refetchLatest(): void {
    if (!this.channelId) return
    const channelId = this.channelId

    this.chatService
      .getMessages(channelId, { limit: HISTORY_PAGE_LIMIT })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envelope) => {
          if (channelId !== this.channelId) return
          const latest = (envelope?.data ?? []) as ChatMessageView[]
          this.mergeUserDisplay(envelope?.meta?.userDisplay)

          const known = new Set(this.messages.map((message) => message._id))
          const overlaps = latest.some((message) => known.has(message._id))

          if (
            this.messages.length > 0 &&
            latest.length > 0 &&
            !overlaps &&
            (envelope?.meta?.hasMore ?? false)
          ) {
            // Gap larger than one page — resetting is the only honest state.
            this.messages = latest
            this.hasMoreHistory = envelope?.meta?.hasMore ?? false
            this.historyCursor = envelope?.meta?.nextCursor ?? null
          } else {
            const fresh = latest.filter(
              (message) =>
                !known.has(message._id) &&
                !(
                  message.clientMessageId &&
                  this.messages.some(
                    (local) =>
                      local.clientMessageId === message.clientMessageId,
                  )
                ),
            )
            if (fresh.length === 0) return
            this.messages = [...this.messages, ...fresh].sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime(),
            )
          }
          this.buildRows()
          this.scrollToBottom()
          this.markRead()
        },
        error: () => {
          /* reconnect refetch is best-effort — next event retries */
        },
      })
  }

  // ---------------------------------------------------------------------------
  // Socket handlers (local append/patch — never through the store)
  // ---------------------------------------------------------------------------

  private onMessageNew(message: ChatMessage): void {
    if (!message || message.channel !== this.channelId) return

    // THREAD REPLY (T-20): replies fan out on the same channel room but must
    // NEVER enter the main flow — `GET /channels/:id/messages` filters
    // `parentMessage: null`, so appending them here would desync the timeline
    // from its own history. They only bump the root's chip (the thread panel
    // renders the reply itself from its own stream).
    if (message.parentMessage) {
      this.patchMessage(String(message.parentMessage), (local) => ({
        ...local,
        threadCount: (local.threadCount ?? 0) + 1,
        threadLastReplyAt: message.createdAt,
      }))
      if (message.sender !== this.getIDCurrentUser()) this.markRead()
      return
    }

    // Dedup by _id, then reconcile own optimistic sends by clientMessageId.
    if (this.messages.some((local) => local._id === message._id)) return

    const optimisticIndex = message.clientMessageId
      ? this.messages.findIndex(
          (local) => local.clientMessageId === message.clientMessageId,
        )
      : -1

    const element = this.scrollContainer?.nativeElement
    const nearBottom =
      !element ||
      element.scrollHeight - element.scrollTop - element.clientHeight <=
        SCROLL_BOTTOM_THRESHOLD_PX

    if (optimisticIndex >= 0) {
      // Own optimistic send confirmed by the fan-out — adopt the persisted
      // message (real _id) and mark it `sent` regardless of ack ordering.
      const updated = [...this.messages]
      updated[optimisticIndex] = { ...message, sendState: 'sent' }
      this.messages = updated
    } else {
      this.messages = [...this.messages, { ...message }]
    }
    this.buildRows()

    const own = message.sender === this.getIDCurrentUser()
    if (own || nearBottom) {
      this.scrollToBottom()
    } else {
      // Arrived while reading history — no scroll hijack; the pill counts it.
      this.newSinceScroll++
      this.showJumpPill = true
      if (!this.firstUnreadMessageId) {
        this.firstUnreadMessageId = message._id
        this.buildRows()
      }
    }
    if (!own) this.markRead()
  }

  private onMessageUpdated(message: ChatMessage): void {
    if (!message || message.channel !== this.channelId) return
    this.patchMessage(message._id, (local) => ({ ...local, ...message }))
  }

  private patchMessage(
    messageId: string,
    patch: (message: ChatMessageView) => ChatMessageView,
  ): void {
    const index = this.messages.findIndex(
      (message) => message._id === messageId,
    )
    if (index === -1) return
    const updated = [...this.messages]
    updated[index] = patch(updated[index])
    this.messages = updated
    this.buildRows()
  }

  /** Still without a server `_id` (or failed) — actions on it would 404. */
  private isPending(message: ChatMessageView): boolean {
    return (
      message.sendState === 'sending' ||
      message.sendState === 'failed' ||
      message._id.startsWith(PENDING_ID_PREFIX)
    )
  }

  private onTyping(
    channelId: string,
    userId: string,
    userName: string | undefined,
    typing: boolean,
  ): void {
    if (channelId !== this.channelId || userId === this.getIDCurrentUser())
      return

    if (this.typingClearTimer) {
      clearTimeout(this.typingClearTimer)
      this.typingClearTimer = null
    }

    if (!typing) {
      this.typingLabel = null
      return
    }

    const name = this.userDisplay[userId]?.name ?? userName ?? ''
    this.typingLabel = this.t('chat.conversation.typing', { name })
    this.typingClearTimer = setTimeout(
      () => (this.typingLabel = null),
      TYPING_CLEAR_MS,
    )
  }

  // ---------------------------------------------------------------------------
  // Sending (optimistic append reconciled by chat:ack + fan-out)
  // ---------------------------------------------------------------------------

  onComposerSend(event: ChatComposerSend): void {
    const content = event?.content ?? ''
    const attachments = event?.attachments ?? []
    if (!content && attachments.length === 0) return
    if (!this.channelId && !this.directUserId) return

    const clientMessageId = this.generateClientMessageId()
    const mentions = event.mentions ?? []
    const now = new Date()
    // Quote reply (RF-30) — the server validates that the quoted message belongs
    // to THIS conversation, so a stale strip is refused rather than mis-attached.
    const replyTo = this.quoteTarget?.messageId
    this.quoteTarget = null
    // Work reference (RF-35) — same carrier rule as replyTo/mentions. The
    // backend re-hydrates the snapshot from `appdb`, so the optimistic row may
    // briefly show the client snapshot before the fan-out replaces it.
    const contextRef = event.contextRef ?? undefined

    // EXACT mirror of the backend split (message.service.ts processSend): one
    // media message per attachment (clientMessageId suffixed -0..-9), plus one
    // TEXT message when content exists (unsuffixed id, appended LAST). Mentions
    // ride the text message when present, otherwise the first attachment.
    const group: ChatMessageView[] = attachments.map((attachment, index) => ({
      _id: `${PENDING_ID_PREFIX}${clientMessageId}-${index}`,
      channel: this.channelId ?? '',
      sender: this.getIDCurrentUser(),
      company: this.getIDCurrentCompany(),
      type: chatMessageTypeFromContentType(attachment.contentType),
      content: '',
      fileUrl: attachment.fileUrl,
      contentType: attachment.contentType,
      fileName: attachment.fileName,
      fileSize: attachment.fileSize,
      clientMessageId: `${clientMessageId}-${index}`,
      threadCount: 0,
      reactions: [],
      mentions: !content && index === 0 ? mentions : [],
      // Mirrors the backend carrier rule: replyTo rides the text message when
      // there is content, otherwise the first attachment.
      replyTo: !content && index === 0 ? replyTo : undefined,
      contextRef: !content && index === 0 ? contextRef : undefined,
      removed: false,
      createdAt: now,
      updatedAt: now,
      sendState: 'sending' as const,
      sendGroup: clientMessageId,
    }))

    if (content) {
      group.push({
        _id: `${PENDING_ID_PREFIX}${clientMessageId}`,
        channel: this.channelId ?? '',
        sender: this.getIDCurrentUser(),
        company: this.getIDCurrentCompany(),
        type: ChatMessageTypeEnum.TEXT,
        content,
        clientMessageId,
        threadCount: 0,
        reactions: [],
        mentions,
        replyTo,
        contextRef,
        removed: false,
        createdAt: now,
        updatedAt: now,
        sendState: 'sending',
        sendGroup: clientMessageId,
      })
    }

    this.messages = [...this.messages, ...group]
    this.buildRows()
    this.scrollToBottom()

    // The composer emptied itself on submit — drop the persisted draft.
    if (this.draftKey) {
      this.store.dispatch(
        ChatActions.clearComposerDraft({ key: this.draftKey }),
      )
    }

    // ONE command for the whole group — the payload survives for retries.
    const base: Record<string, unknown> = {
      clientMessageId,
      ...(content ? { content } : {}),
      ...(mentions.length ? { mentions } : {}),
      ...(attachments.length ? { attachments } : {}),
      ...(replyTo ? { replyTo } : {}),
      ...(contextRef ? { contextRef } : {}),
    }
    this.sendPayloads.set(
      clientMessageId,
      this.channelId
        ? { channelId: this.channelId, ...base }
        : { directUserId: this.directUserId, ...base },
    )

    this.dispatchSend(clientMessageId)
  }

  /** Failed row action — re-sends the SAME payload/clientMessageId (idempotent server-side). */
  retrySend(row: TimelineRow): void {
    const message = row.message
    if (!message?.sendGroup || message.sendState !== 'failed') return
    if (!this.sendPayloads.has(message.sendGroup)) return
    this.patchBySendGroup(message.sendGroup, (local) =>
      local.sendState === 'failed' ? { ...local, sendState: 'sending' } : local,
    )
    this.dispatchSend(message.sendGroup)
  }

  /**
   * Read indication for one of MY messages in a DIRECT conversation
   * (RF-33/AC-29): true once the counterpart's read watermark reaches the
   * message. Channels and groups never show it — the backend only broadcasts
   * `chat:read:updated` for direct conversations, so `counterpartLastReadAt`
   * stays undefined there.
   */
  isReadByCounterpart(row: TimelineRow): boolean {
    if (!this.isDirect || !row?.own) return false

    const readAt = this.conversation?.counterpartLastReadAt
    const sentAt = row.message?.createdAt
    if (!readAt || !sentAt) return false

    return new Date(sentAt).getTime() <= new Date(readAt).getTime()
  }

  /** Failed row action — drops the whole failed optimistic group locally. */
  discardSend(row: TimelineRow): void {
    const message = row.message
    if (!message || message.sendState !== 'failed') return
    if (message.sendGroup) {
      // Members already confirmed by the fan-out (sent) are kept.
      this.messages = this.messages.filter(
        (local) =>
          !(
            local.sendGroup === message.sendGroup &&
            local.sendState === 'failed'
          ),
      )
      this.sendPayloads.delete(message.sendGroup)
    } else {
      this.messages = this.messages.filter((local) => local._id !== message._id)
    }
    this.buildRows()
  }

  onTypingChange(typing: boolean): void {
    if (!this.channelId) return
    this.chatSocketService.typing(
      this.channelId,
      typing,
      this.getNameCurrentUser(),
    )
  }

  /** Composer content mutated — schedule the debounced draft snapshot (T-19). */
  onDraftChange(): void {
    this.draftPersist$.next()
  }

  toggleReaction(row: TimelineRow, emoji: string): void {
    const message = row.message
    if (!message || message.deleted || this.isPending(message)) return
    // Read-only preview (public channel, not a member): reacting would be
    // refused with notChannelMember — join first.
    if (this.isChannelPreview) return

    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.REACTION_TOGGLE, {
        messageId: message._id,
        emoji,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          /* authoritative reactions arrive on chat:reaction:updated */
        },
        error: (ack) =>
          this.alertService.error(
            this.translateAckError(ack?.errorKey, 'chat.errors.commandFailed'),
          ),
      })
  }

  // ---------------------------------------------------------------------------
  // Message actions (T-20 · RF-09…RF-14, RF-30, RF-31)
  // ---------------------------------------------------------------------------

  /** Quote reply (RF-30) — strip above the composer, `replyTo` on the next send. */
  startQuoteReply(row: TimelineRow): void {
    const message = row.message
    if (!message || message.deleted || row.pending) return
    this.quoteTarget = {
      messageId: message._id,
      authorName: row.authorName ?? '',
      excerpt: this.buildExcerpt(row),
    }
  }

  cancelQuoteReply(): void {
    this.quoteTarget = null
  }

  /**
   * "Responder no privado" (RF-30) — opens the DM with the sender and prefills
   * its composer draft with the quote and the origin conversation.
   *
   * CONTRACT NOTE: `replyTo` cannot be reused here — the server requires the
   * quoted message to belong to the destination conversation
   * (message.service.ts getChannelMessage), so a cross-conversation quote would
   * be refused. The origin is therefore carried as text in the prefilled draft.
   */
  replyInPrivate(row: TimelineRow): void {
    const message = row.message
    if (!message || row.own || row.pending || this.isDirect) return

    const targetUserId = message.sender
    if (!targetUserId || targetUserId === this.getIDCurrentUser()) return

    const content = `${this.t('chat.conversation.replyInPrivatePrefix', {
      author: row.authorName ?? '',
      channel: this.headerName,
    })}\n"${this.buildExcerpt(row)}"\n\n`

    const directKey = [this.getIDCurrentUser(), targetUserId].sort().join(':')
    const existing = this.conversations.find(
      (conversation) =>
        conversation.type === ChatChannelTypeEnum.DIRECT &&
        conversation.directKey === directKey,
    )

    // The draft is stored BEFORE the switch so the destination pane restores it
    // on its first change-detection pass (draft key = channelId | direct:{id}).
    this.store.dispatch(
      ChatActions.setComposerDraft({
        key: existing ? existing._id : `direct:${targetUserId}`,
        draft: { content, mentions: [] },
      }),
    )

    if (existing) {
      this.store.dispatch(
        ChatActions.openConversation({ channelId: existing._id }),
      )
    } else {
      this.store.dispatch(
        ChatActions.openDirectDraft({ userId: targetUserId, directKey }),
      )
    }
  }

  /** Thread panel (RF-12) — the main-flow chip and the toolbar both land here. */
  openThread(row: TimelineRow): void {
    const message = row.message
    if (!message || row.pending || !this.channelId) return
    this.openThreadRootId = message._id
    this.threadOpenChange.emit(message._id)
    // Opening acknowledges the replies known so far (unseen accent clears).
    this.store.dispatch(
      ChatActions.setThreadSeen({
        messageId: message._id,
        count: message.threadCount ?? 0,
      }),
    )
  }

  closeThread(): void {
    this.openThreadRootId = null
    this.threadOpenChange.emit(null)
  }

  // --- Edit / delete (own messages only — enforced server-side too) ----------

  startEdit(row: TimelineRow): void {
    const message = row.message
    if (!message || !row.own || row.pending || message.deleted) return
    // Only TEXT bodies are editable — MESSAGE_EDIT rejects an empty content and
    // media messages carry none.
    if (message.type !== ChatMessageTypeEnum.TEXT) return
    this.editingMessageId = message._id
    this.editDraft = message.content ?? ''
  }

  cancelEdit(): void {
    this.editingMessageId = null
    this.editDraft = ''
  }

  saveEdit(): void {
    const messageId = this.editingMessageId
    const content = (this.editDraft ?? '').trim()
    if (!messageId) return
    if (!content) {
      this.alertService.error(this.t('chat.errors.emptyMessage'))
      return
    }

    this.cancelEdit()
    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.MESSAGE_EDIT, { messageId, content })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        // The authoritative content + editedAt arrive on chat:message:updated.
        next: () => {
          /* nothing to do locally */
        },
        error: (ack) =>
          this.alertService.error(
            this.translateAckError(ack?.errorKey, 'chat.errors.commandFailed'),
          ),
      })
  }

  /** Enter saves, Shift+Enter breaks the line, Escape cancels (composer parity). */
  onEditKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      this.cancelEdit()
      return
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.saveEdit()
    }
  }

  confirmDelete(row: TimelineRow): void {
    const message = row.message
    if (!message || !row.own || row.pending || message.deleted) return

    // Shared Hyper-styled confirmation (already localized in lib-trend-core).
    this.dialog
      .open(ChatConfirmDialogComponent, {
        width: '480px',
        data: this.t('chat.conversation.deleteConfirm'),
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed !== true) return
        this.chatSocketService
          .sendCommand(ChatCommandTypeEnum.MESSAGE_DELETE, {
            messageId: message._id,
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            // The tombstone arrives on chat:message:deleted.
            next: () => {
              /* nothing to do locally */
            },
            error: (ack) =>
              this.alertService.error(
                this.translateAckError(
                  ack?.errorKey,
                  'chat.errors.commandFailed',
                ),
              ),
          })
      })
  }

  // --- Forward / save / pin ---------------------------------------------------

  openForward(row: TimelineRow): void {
    const message = row.message
    if (!message || message.deleted || row.pending) return

    const data: ChatForwardDialogData = {
      messageId: message._id,
      preview: this.buildExcerpt(row),
      sourceChannelId: this.channelId,
    }
    this.dialog.open(ChatForwardDialogComponent, { width: '520px', data })
  }

  /** The viewer over every image of the loaded timeline, starting on this one (DEV-2062). */
  openImage(row: TimelineRow): void {
    const message = row.message
    if (!message?.fileUrl) return
    const messages = this.rows
      .map((entry) => entry.message)
      .filter((entry): entry is ChatMessageView => !!entry)
    openChatImageViewer(this.dialog, collectChatImages(messages), message._id || message.clientMessageId || '')
  }

  /** Bookmark toggle (RF-14) — personal, no fan-out, so the local flip is truth. */
  toggleSaved(row: TimelineRow): void {
    const message = row.message
    if (!message || row.pending) return

    const nextSaved = !row.saved
    this.store.dispatch(
      ChatActions.applySavedToggle({
        messageId: message._id,
        saved: nextSaved,
      }),
    )

    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.SAVED_TOGGLE, { messageId: message._id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.store.dispatch(ChatActions.loadSaved()),
        error: (ack) => {
          this.store.dispatch(
            ChatActions.applySavedToggle({
              messageId: message._id,
              saved: !nextSaved,
            }),
          )
          this.alertService.error(
            this.translateAckError(ack?.errorKey, 'chat.errors.commandFailed'),
          )
        },
      })
  }

  /** Pin/unpin (RF-31) — the authoritative list arrives on `chat:pin:updated`. */
  togglePin(row: TimelineRow): void {
    const message = row.message
    if (!message || row.pending || !this.channelId) return

    const type = row.pinned
      ? ChatCommandTypeEnum.CHANNEL_UNPIN_MESSAGE
      : ChatCommandTypeEnum.CHANNEL_PIN_MESSAGE

    this.chatSocketService
      .sendCommand(type, { channelId: this.channelId, messageId: message._id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          /* pinnedMessages update arrives on chat:pin:updated */
        },
        // Surfaces chat.errors.pinLimitReached (50 pins) and notPinOwner.
        error: (ack) =>
          this.alertService.error(
            this.translateAckError(ack?.errorKey, 'chat.errors.commandFailed'),
          ),
      })
  }

  /** Pinned bar action — only the pinner or a conversation ADMIN may unpin. */
  unpin(pinned: ChatPinnedRow): void {
    if (!this.channelId) return
    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.CHANNEL_UNPIN_MESSAGE, {
        channelId: this.channelId,
        messageId: pinned.messageId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          /* pinnedMessages update arrives on chat:pin:updated */
        },
        error: (ack) =>
          this.alertService.error(
            this.translateAckError(ack?.errorKey, 'chat.errors.commandFailed'),
          ),
      })
  }

  // --- Copy / jump -----------------------------------------------------------

  /** "Copiar texto" (RF-30) — the raw markdown body, not the rendered HTML. */
  copyText(row: TimelineRow): void {
    const text = row.message?.content ?? ''
    if (!text) return

    const clipboard = navigator.clipboard
    if (!clipboard?.writeText) {
      // Insecure context or unsupported browser — never fail silently.
      this.alertService.error(this.t('chat.conversation.copyFailed'))
      return
    }
    clipboard
      .writeText(text)
      .then(() => this.alertService.success(this.t('chat.conversation.copied')))
      .catch(() =>
        this.alertService.error(this.t('chat.conversation.copyFailed')),
      )
  }

  /**
   * Reveals a message in the timeline and highlights it. Targets outside the
   * loaded window are sought by paging back up to JUMP_MAX_PAGES times before
   * giving up with a localized notice (quote/pin/saved jumps share this).
   */
  jumpToMessage(messageId: string): void {
    if (!messageId) return
    if (this.messages.some((message) => message._id === messageId)) {
      this.revealMessage(messageId)
      return
    }
    this.seekMessage(messageId, JUMP_MAX_PAGES)
  }

  jumpToQuote(row: TimelineRow): void {
    const replyTo = row.message?.replyTo
    if (replyTo) this.jumpToMessage(String(replyTo))
  }

  // --- In-conversation search ------------------------------------------------

  search(term: string): void {
    this.searchTerm = term ?? ''
    const normalized = this.searchTerm.trim()
    if (!normalized) this.searchHits = []

    // Set here rather than inside the pipe: the request only starts after the
    // debounce, and a flag left idle during that window flashes "no results"
    // before the spinner ever appears.
    this.searching = normalized.length >= SEARCH_MIN_LENGTH
    this.searchTerms.next(normalized)
  }

  clearSearch(): void {
    this.search('')
  }

  /** Settled, long enough to have actually run, and nothing came back. */
  get noSearchHits(): boolean {
    return (
      !this.searching &&
      this.searchTerm.trim().length >= SEARCH_MIN_LENGTH &&
      this.searchHits.length === 0
    )
  }

  openSearchHit(hit: ChatConversationSearchHit): void {
    this.jumpToMessage(hit.messageId)
  }

  /** Segments are positional and their text repeats — the index alone is not stable. */
  trackSegment(index: number, segment: ChatHighlightSegment): string {
    return `${index}:${segment.text}`
  }

  private buildSearchHits(
    messages: ChatMessage[],
    userDisplay: ChatUserDisplayMap,
    term: string,
  ): ChatConversationSearchHit[] {
    return messages.map((message) => ({
      messageId: String(message._id),
      authorName:
        userDisplay[String(message.sender ?? '')]?.name ??
        this.t('chat.saved.unknownAuthor'),
      timeLabel: this.formatTime(message.createdAt),
      // Stripped before the window is cut — the markers are not searchable text
      // and would otherwise eat the radius around the match.
      segments: highlightSegments(
        buildExcerpt(stripChatMarkdown(message.content), term, SEARCH_EXCERPT_RADIUS),
        term,
      ),
    }))
  }

  /** Wall-clock time of a hit, in the viewer's own clock. */
  private formatTime(value: Date | string | undefined): string {
    const date = value ? new Date(value) : null
    if (!date || Number.isNaN(date.getTime())) return ''

    const pad = (part: number) => String(part).padStart(2, '0')
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  trackPinned(_index: number, pinned: ChatPinnedRow): string {
    return pinned.messageId
  }

  /**
   * MESSAGE_SEND keyed by the group's base clientMessageId — shared by the
   * first attempt and retries, so a server-side duplicate is impossible.
   * Reconciliation is order-safe: ack success marks the whole group `sent`
   * (works whether or not the fan-out already swapped in the persisted _id),
   * and an error ack never demotes a member the fan-out proved delivered.
   */
  /**
   * The token half of the server's `buildChatMessagePreview`: media type of the
   * last attachment, or the work-reference token for a bare reference send.
   * Tokens are what the list translates per user (chat-preview.util.ts).
   */
  private attachmentPreviewToken(
    contentType: string | undefined,
    hasContextRef: boolean,
  ): string {
    if (!contentType) return hasContextRef ? '[contextRef]' : ''
    switch (chatMessageTypeFromContentType(contentType)) {
      case ChatMessageTypeEnum.IMAGE:
        return '[image]'
      case ChatMessageTypeEnum.AUDIO:
        return '[audio]'
      case ChatMessageTypeEnum.VIDEO:
        return '[video]'
      default:
        return '[file]'
    }
  }

  private dispatchSend(groupId: string): void {
    const payload = this.sendPayloads.get(groupId)
    if (!payload) return

    this.chatSocketService
      .sendCommand(ChatCommandTypeEnum.MESSAGE_SEND, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.sendPayloads.delete(groupId)
          this.patchBySendGroup(groupId, (local) => ({
            ...local,
            sendState: 'sent',
          }))
          if (this.channelId) {
            // Mirrors the backend preview rule EXACTLY (buildChatMessagePreview):
            // the TEXT message is persisted last, so content wins; otherwise the
            // language-neutral token of the last attachment's media type — the
            // same value the server persists and fans out to everyone else, so
            // the sender's row cannot drift ("[image]" here, "IMG_1.jpg" there).
            const content =
              typeof payload['content'] === 'string'
                ? (payload['content'] as string)
                : ''
            const attachments = Array.isArray(payload['attachments'])
              ? (payload['attachments'] as Array<{ contentType?: string }>)
              : []
            const preview = content
              ? this.buildPreview(content)
              : this.attachmentPreviewToken(
                  attachments[attachments.length - 1]?.contentType,
                  !!payload['contextRef'],
                )
            this.store.dispatch(
              ChatActions.setConversationPreview({
                channelId: this.channelId,
                preview,
              }),
            )
          } else {
            // DIRECT draft — the channel now exists server-side; the reducer
            // promotes the draft as soon as the refreshed bootstrap carries it.
            this.store.dispatch(ChatActions.loadBootstrap())
          }
        },
        error: (ack) => {
          this.patchBySendGroup(groupId, (local) =>
            // Fan-out already delivered it — a late error ack must not lie.
            local.sendState === 'sending' ||
            local._id.startsWith(PENDING_ID_PREFIX)
              ? { ...local, sendState: 'failed' }
              : local,
          )
          this.alertService.error(
            this.translateAckError(ack?.errorKey, 'chat.errors.sendFailed'),
          )
        },
      })
  }

  /** Patches every optimistic member of a send group (see ChatMessageView.sendGroup). */
  private patchBySendGroup(
    groupId: string,
    patch: (message: ChatMessageView) => ChatMessageView,
  ): void {
    let touched = false
    const updated = this.messages.map((message) => {
      if (message.sendGroup !== groupId) return message
      touched = true
      return patch(message)
    })
    if (!touched) return
    this.messages = updated
    this.buildRows()
  }

  retryLoad(): void {
    this.loadInitial()
  }

  // ---------------------------------------------------------------------------
  // Drag-and-drop attachments (T-33) — files land in the composer's tray
  // ---------------------------------------------------------------------------

  onDragEnter(event: DragEvent): void {
    if (this.composerDisabled || !this.dragHasFiles(event)) return
    event.preventDefault()
    this.dragDepth++
    this.dragActive = true
  }

  onDragOver(event: DragEvent): void {
    if (!this.dragActive) return
    // Required — without it the browser navigates to the dropped file.
    event.preventDefault()
  }

  onDragLeave(_event: DragEvent): void {
    if (!this.dragActive) return
    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.dragActive = false
  }

  onDrop(event: DragEvent): void {
    if (!this.dragActive) return
    event.preventDefault()
    this.dragDepth = 0
    this.dragActive = false
    const files = event.dataTransfer?.files
    if (files?.length) {
      // Same validated entry point as the picker/paste — limits run there.
      this.composer?.addFiles(files)
    }
  }

  private dragHasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files')
  }

  trackRow(_index: number, row: TimelineRow): string {
    return row.key
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private resetTimeline(): void {
    // A READ deferred for the conversation being left must not be sent later.
    this.attentiveRead.cancel()
    this.messages = []
    this.rows = []
    this.pinnedRows = []
    this.unreadAtOpen = 0
    this.firstUnreadMessageId = null
    this.newSinceScroll = 0
    this.showJumpPill = false
    this.loadingHistory = false
    this.loadingOlder = false
    this.loadError = false
    this.hasMoreHistory = false
    this.historyCursor = null
    this.typingLabel = null
    this.conversation = null
    this.dragActive = false
    this.dragDepth = 0
    // T-20 per-conversation UI state never leaks across a switch.
    this.openThreadRootId = null
    this.quoteTarget = null
    this.editingMessageId = null
    this.editDraft = ''
    this.highlightedMessageId = null
    if (this.highlightClearTimer) {
      clearTimeout(this.highlightClearTimer)
      this.highlightClearTimer = null
    }
    // Failed rows are gone with the timeline — their kept payloads go too.
    this.sendPayloads.clear()
  }

  // ---------------------------------------------------------------------------
  // Jump-to-message (T-20)
  // ---------------------------------------------------------------------------

  /** Scrolls the row into view and flashes the highlight. */
  private revealMessage(messageId: string): void {
    this.highlightedMessageId = messageId
    if (this.highlightClearTimer) clearTimeout(this.highlightClearTimer)
    this.highlightClearTimer = setTimeout(() => {
      this.highlightedMessageId = null
      this.highlightClearTimer = null
    }, JUMP_HIGHLIGHT_MS)

    // Deferred so the highlight class is in the DOM before scrolling.
    setTimeout(() => {
      const container = this.scrollContainer?.nativeElement
      const element = container?.querySelector<HTMLElement>(
        `[data-message-id="${messageId}"]`,
      )
      if (!container || !element) return
      container.scrollTop = element.offsetTop - container.clientHeight / 3
    })
  }

  /** Pages back looking for a jump target; gives up after `remaining` pages. */
  private seekMessage(messageId: string, remaining: number): void {
    if (remaining <= 0 || !this.hasMoreHistory) {
      this.alertService.info(this.t('chat.conversation.jumpNotFound'))
      return
    }
    this.loadOlder((loadedNew) => {
      if (this.messages.some((message) => message._id === messageId)) {
        this.revealMessage(messageId)
        return
      }
      if (!loadedNew) {
        this.alertService.info(this.t('chat.conversation.jumpNotFound'))
        return
      }
      this.seekMessage(messageId, remaining - 1)
    })
  }

  /**
   * READ resets lastReadAt + badge; optimistic clear keeps the UI immediate.
   * Sent only while the page is visible and focused (RF-22): otherwise it waits,
   * and the messages stay unread, until attention returns.
   */
  private markRead(): void {
    if (!this.channelId) return
    // Preview of a public channel: no membership row exists, so READ would be
    // refused with notChannelMember — and there is nothing to mark anyway.
    if (this.isChannelPreview) return
    const channelId = this.channelId
    this.attentiveRead.request(() => this.sendRead(channelId))
  }

  private sendRead(channelId: string): void {
    // The pane may have moved on while the READ was deferred.
    if (channelId !== this.channelId || this.isChannelPreview) return
    this.store.dispatch(ChatActions.clearUnread({ channelId }))
    this.chatSocketService.sendCommand(ChatCommandTypeEnum.READ, { channelId })
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const element = this.scrollContainer?.nativeElement
      if (element) element.scrollTop = element.scrollHeight
    })
  }

  private mergeUserDisplay(userDisplay?: ChatUserDisplayMap): void {
    if (!userDisplay) return
    this.userDisplay = { ...this.userDisplay, ...userDisplay }
    this.rebuildMentionMembers()
  }

  // ---------------------------------------------------------------------------
  // Mention members + composer drafts (T-19)
  // ---------------------------------------------------------------------------

  /** Draft-store key: channelId, or `direct:{userId}` while the DM is a draft. */
  private get conversationKey(): string | null {
    if (this.channelId) return this.channelId
    return this.directUserId ? `direct:${this.directUserId}` : null
  }

  /**
   * Mention roster for PUBLIC/PRIVATE channels — the bootstrap only carries
   * `members` for DM/GROUP, so channels fetch theirs from
   * `GET /channels/:id/members` (once per open channel; membership fan-outs
   * force a refresh). Guarded by `rosterChannelId` so store re-emissions never
   * re-fire the request.
   */
  private channelRosterIds: string[] = []
  private rosterChannelId: string | null = null

  private get isChannelConversation(): boolean {
    const type = this.conversation?.type
    return (
      type === ChatChannelTypeEnum.PUBLIC ||
      type === ChatChannelTypeEnum.PRIVATE
    )
  }

  private maybeLoadChannelRoster(force = false): void {
    if (!this.channelId || !this.isChannelConversation) return
    if (!force && this.rosterChannelId === this.channelId) return

    const channelId = this.channelId
    this.rosterChannelId = channelId
    this.chatService
      .getChannelMembers(channelId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (envelope) => {
          // The pane may have switched conversations while the request flew.
          if (this.channelId !== channelId) return
          this.channelRosterIds = (envelope?.data ?? []).map(
            (member) => member.user,
          )
          // Display names go through the store (merge-only) so every other
          // chat surface benefits — same route the invite dialog uses.
          const userDisplay = envelope?.meta?.userDisplay
          if (userDisplay && Object.keys(userDisplay).length > 0) {
            this.store.dispatch(ChatActions.mergeUserDisplay({ userDisplay }))
          }
          this.rebuildMentionMembers()
        },
        error: () => {
          // Cleared so the next conversation/store emission retries.
          if (this.channelId === channelId) this.rosterChannelId = null
        },
      })
  }

  /** Active conversation's members resolvable via userDisplay, minus self. */
  private rebuildMentionMembers(): void {
    const currentUserId = this.getIDCurrentUser()
    const memberIds = this.isDraft
      ? [this.directUserId]
      : this.isChannelConversation
        ? this.channelRosterIds
        : (this.conversation?.members ?? [])

    this.mentionMembers = memberIds
      .filter((id): id is string => !!id && id !== currentUserId)
      .map((id) => ({
        id,
        name: this.userDisplay[id]?.name ?? '',
        image: this.userDisplay[id]?.image,
      }))
      .filter((member) => !!member.name)
      // Channel rosters can be big — a stable A→Z list is scannable where
      // insertion order is noise. Groups/DMs are small; sorting is harmless.
      .sort((a, b) => a.name.localeCompare(b.name))
  }

  /** Snapshots the composer into the store under the CURRENT draftKey. */
  private persistDraft(): void {
    if (!this.draftKey || !this.composer) return
    const snapshot = this.composer.getDraftSnapshot()
    if (snapshot.content.length === 0) {
      if (this.composerDrafts[this.draftKey]) {
        this.store.dispatch(
          ChatActions.clearComposerDraft({ key: this.draftKey }),
        )
      }
      return
    }
    this.store.dispatch(
      ChatActions.setComposerDraft({ key: this.draftKey, draft: snapshot }),
    )
  }

  /** Restores the persisted draft of the hosted conversation into the composer. */
  private restoreDraft(): void {
    // Deferred: ngOnChanges runs mid change-detection and ngAfterViewInit runs
    // right after it — mutating the composer synchronously would trip NG0100.
    setTimeout(() => {
      if (!this.composer) return
      this.composer.setDraftSnapshot(
        this.draftKey ? (this.composerDrafts[this.draftKey] ?? null) : null,
      )
    })
  }

  private resolveConversationName(conversation: ChatConversation): string {
    const currentUserId = this.getIDCurrentUser()
    switch (conversation.type) {
      case ChatChannelTypeEnum.DIRECT: {
        const otherId =
          (conversation.members ?? []).find((id) => id !== currentUserId) ??
          currentUserId
        return (
          this.userDisplay[otherId]?.name ??
          this.t('chat.bubble.directConversation')
        )
      }
      case ChatChannelTypeEnum.GROUP: {
        if (conversation.name) return conversation.name
        const names = (conversation.members ?? [])
          .filter((id) => id !== currentUserId)
          .map((id) => this.userDisplay[id]?.name)
          .filter((name): name is string => !!name)
        return names.length > 0
          ? names.slice(0, 3).join(', ')
          : this.t('chat.bubble.groupConversation')
      }
      default:
        return conversation.name ?? this.t('chat.bubble.channelConversation')
    }
  }

  /**
   * Forwarded banner (RF-13): named when the original-author snapshot resolves
   * through userDisplay, plain "Encaminhada" as the fallback (older copies
   * persisted before the snapshot, or an author display that vanished).
   */
  private resolveForwardedLabel(message: ChatMessageView): string | undefined {
    if (!message.forwardedFrom && !message.forwardedFromSender) return undefined
    const name = message.forwardedFromSender
      ? this.userDisplay[message.forwardedFromSender]?.name
      : undefined
    return name
      ? this.t('chat.conversation.forwardedFrom', { name })
      : this.t('chat.conversation.forwarded')
  }

  /** Rebuilds the flattened render rows (dividers + precomputed message VMs). */
  private buildRows(): void {
    const rows: TimelineRow[] = []
    let previousDate = ''
    let previousSender = ''
    const pinnedIds = new Set(
      (this.conversation?.pinnedMessages ?? []).map((pinned) =>
        String(pinned.message),
      ),
    )

    for (const message of this.messages) {
      const created = new Date(message.createdAt)
      const dateKey = created.toDateString()

      if (dateKey !== previousDate) {
        rows.push({
          kind: 'divider',
          key: `divider:${dateKey}`,
          label: this.dayLabel(created),
        })
        previousDate = dateKey
        previousSender = ''
      }

      // "Novas mensagens" line — anchored where reading stopped. Resetting the
      // sender forces the anchored message to show its author even mid-run:
      // the line breaks the visual group, so the row below must reintroduce
      // who is talking.
      if (message._id === this.firstUnreadMessageId) {
        rows.push({ kind: 'unread', key: 'unread-line' })
        previousSender = ''
      }

      const display = this.userDisplay[message.sender]
      const authorName =
        display?.name ?? this.t('chat.bubble.directConversation')
      const quote = this.resolveQuote(message)
      const isMedia = message.type !== ChatMessageTypeEnum.TEXT
      const mentionIds = message.mentions ?? []
      const currentUserId = this.getIDCurrentUser()

      rows.push({
        fileIcon: isMedia
          ? chatFileIcon(message.contentType, message.fileName)
          : undefined,
        fileSizeLabel:
          isMedia && message.fileSize
            ? formatChatFileSize(message.fileSize)
            : undefined,
        kind: 'message',
        key: message._id,
        message,
        authorName,
        authorInitial: resolveChatInitials(authorName || '?'),
        authorImage: display?.image,
        timeLabel: formatDate(created, 'HH:mm', 'en-US'),
        showAuthor: message.sender !== previousSender,
        own: message.sender === this.getIDCurrentUser(),
        pending: this.isPending(message),
        ...quote,
        reactions: (message.reactions ?? [])
          .filter((reaction) => (reaction.users ?? []).length > 0)
          .map((reaction) => ({
            emoji: reaction.emoji,
            count: reaction.users.length,
            mine: reaction.users.includes(this.getIDCurrentUser()),
          })),
        // T-20 action state -----------------------------------------------------
        saved: this.savedIds.has(message._id),
        pinned: pinnedIds.has(message._id),
        threadUnseen:
          (message.threadCount ?? 0) > (this.threadSeen[message._id] ?? 0),
        threadTimeLabel: message.threadLastReplyAt
          ? formatDate(new Date(message.threadLastReplyAt), 'HH:mm', 'en-US')
          : undefined,
        copyText:
          message.type === ChatMessageTypeEnum.TEXT
            ? (message.content ?? '')
            : '',
        // Mention chips (names resolved on data arrival, like authorName) and
        // the row tint when one of them is me.
        mentionRender: mentionIds
          .map((id) => ({
            name: this.userDisplay[id]?.name ?? '',
            self: id === currentUserId,
          }))
          .filter((mention) => !!mention.name),
        mentionsMe: mentionIds.includes(currentUserId),
        forwardedLabel: this.resolveForwardedLabel(message),
      })
      previousSender = message.sender
    }

    this.rows = rows
    this.topReactions = this.buildTopReactions()
    this.pinnedRows = this.buildPinnedRows()
  }

  /**
   * The user's three most-used emojis (RF-11/AC-09). Authoritative ranking is
   * the `chat:topemojis:{userId}` zset, read by the bootstrap endpoint and kept
   * in the store — it spans the user's whole history, not the loaded window, so
   * the bar survives a reload and a conversation switch. The defaults fill any
   * remaining slot (a user who has never reacted has an empty ranking) so the
   * bar always offers three.
   */
  private buildTopReactions(): string[] {
    const ranked = this.topEmojis.slice(0, QUICK_REACTION_COUNT)

    for (const fallback of DEFAULT_QUICK_REACTIONS) {
      if (ranked.length >= QUICK_REACTION_COUNT) break
      if (!ranked.includes(fallback)) ranked.push(fallback)
    }
    return ranked
  }

  /**
   * Pinned bar entries (RF-31/AC-27) in pin order. Pins older than the loaded
   * window keep a neutral label — the jump pages back to find them.
   */
  private buildPinnedRows(): ChatPinnedRow[] {
    return (this.conversation?.pinnedMessages ?? []).map((pinned) => {
      const messageId = String(pinned.message)
      const message = this.messages.find((local) => local._id === messageId)
      if (!message || message.deleted) {
        return {
          messageId,
          authorName: '',
          excerpt: this.t('chat.conversation.pinnedUnresolved'),
          unresolved: true,
        }
      }
      return {
        messageId,
        authorName: this.userDisplay[message.sender]?.name ?? '',
        excerpt:
          message.type === ChatMessageTypeEnum.TEXT
            ? this.buildPreview(message.content)
            : (message.fileName ??
              this.t('chat.conversation.pinnedAttachment')),
        unresolved: false,
      }
    })
  }

  /**
   * Short single-line excerpt used by the quote strip, the forward dialog and
   * the "responder no privado" draft — all plain-text surfaces, so the markdown
   * markers come off. "Copiar texto" does NOT come through here: it copies
   * `message.content` verbatim, which is the raw body the author wrote.
   */
  private buildExcerpt(row: TimelineRow): string {
    const message = row.message
    const source =
      message.type === ChatMessageTypeEnum.TEXT
        ? stripChatMarkdown(message.content)
        : (message.fileName ?? this.t('chat.conversation.pinnedAttachment'))
    const flat = source.replace(/\s+/g, ' ').trim()
    return flat.length > QUOTE_EXCERPT_MAX_LENGTH
      ? `${flat.slice(0, QUOTE_EXCERPT_MAX_LENGTH)}…`
      : flat
  }

  /**
   * `replyTo` is NOT hydrated by the backend — quotes resolve client-side from
   * already-loaded messages, otherwise a neutral placeholder shows.
   */
  private resolveQuote(message: ChatMessageView): Partial<TimelineRow> {
    if (!message.replyTo) return {}
    const quoted = this.messages.find((local) => local._id === message.replyTo)
    if (!quoted || quoted.deleted) return { quoteMissing: true }
    return {
      quoteAuthor: this.userDisplay[quoted.sender]?.name ?? '',
      quoteText:
        quoted.type === ChatMessageTypeEnum.TEXT
          ? this.buildPreview(quoted.content)
          : (quoted.fileName ?? this.t('chat.conversation.pinnedAttachment')),
    }
  }

  /** Plain-text preview for the pinned bar, quotes and the sidebar row. */
  private buildPreview(content: string): string {
    const flat = stripChatMarkdown(content)
    return flat.length > PREVIEW_MAX_LENGTH
      ? `${flat.slice(0, PREVIEW_MAX_LENGTH)}…`
      : flat
  }

  private dayLabel(date: Date): string {
    const now = new Date()
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)

    if (date.toDateString() === now.toDateString())
      return this.t('chat.conversation.today')
    if (date.toDateString() === yesterday.toDateString())
      return this.t('chat.conversation.yesterday')
    return formatDate(date, 'dd/MM/yyyy', 'en-US')
  }

  /** Translates a backend/synthetic ack errorKey, falling back when unknown. */
  private translateAckError(
    errorKey: string | undefined,
    fallbackKey: string,
  ): string {
    if (errorKey) {
      const translated = this.t(errorKey)
      if (translated !== errorKey) return translated
    }
    return this.t(fallbackKey)
  }

  private generateClientMessageId(): string {
    const cryptoObj: Crypto | undefined = globalThis.crypto
    if (cryptoObj?.randomUUID) return cryptoObj.randomUUID()
    return `cmid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  }
}
